'use client';

import { useState, useCallback } from 'react';
import {
  parseFormulaTemplate,
  formulaTemplateToSource,
  type FormulaTemplate,
  type ArithmeticPart,
  type BatchAggFn,
  type ConcatSegment,
  type ConditionalClause,
} from '@capture/shared';
import { FunctionEditor } from './FunctionEditor';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ComputeVisualEditorProps {
  formula: string;
  onChange: (source: string) => void;
  fieldNames: string[];
  scope?: 'record' | 'batch';
}

// ---------------------------------------------------------------------------
// Template kind labels
// ---------------------------------------------------------------------------

type TemplateKind = FormulaTemplate['kind'];

const TEMPLATE_OPTIONS: { kind: TemplateKind; label: string; batchOnly?: boolean }[] = [
  { kind: 'static', label: 'Static Value' },
  { kind: 'arithmetic', label: 'Arithmetic' },
  { kind: 'conditional', label: 'Conditional' },
  { kind: 'concatenation', label: 'Concatenation' },
  { kind: 'batch-aggregation', label: 'Batch Aggregation', batchOnly: true },
  { kind: 'function', label: 'Function' },
];

const BATCH_FNS: BatchAggFn[] = [
  'BATCH_COUNT',
  'BATCH_SUM',
  'BATCH_AVG',
  'BATCH_MIN',
  'BATCH_MAX',
  'BATCH_FIRST',
  'BATCH_LAST',
];

const ARITH_OPS: Array<'+' | '-' | '*' | '/'> = ['+', '-', '*', '/'];

// ---------------------------------------------------------------------------
// Sub-editors
// ---------------------------------------------------------------------------

function StaticEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (source: string) => void;
}) {
  // Strip surrounding quotes for display
  const isString = /^['"]/.test(value);
  const displayValue = isString ? value.slice(1, -1) : value;

  function handleChange(raw: string) {
    // Detect if user typed a number
    if (/^-?\d+(\.\d+)?$/.test(raw)) {
      onChange(raw);
    } else {
      onChange(`'${raw}'`);
    }
  }

  return (
    <div className="compute-visual__static">
      <label>
        Value
        <input
          type="text"
          value={displayValue}
          onChange={(e) => handleChange(e.target.value)}
        />
      </label>
    </div>
  );
}

function ArithmeticEditor({
  parts,
  onChange,
  fieldNames,
}: {
  parts: ArithmeticPart[];
  onChange: (source: string) => void;
  fieldNames: string[];
}) {
  function updatePart(index: number, updated: ArithmeticPart) {
    const next = [...parts];
    next[index] = updated;
    onChange(formulaTemplateToSource({ kind: 'arithmetic', parts: next }));
  }

  function addOperand() {
    const next: ArithmeticPart[] = [
      ...parts,
      { type: 'operator', op: '+' },
      { type: 'field', name: fieldNames[0] ?? 'field' },
    ];
    onChange(formulaTemplateToSource({ kind: 'arithmetic', parts: next }));
  }

  return (
    <div className="compute-visual__arithmetic">
      {parts.map((part, i) => {
        if (part.type === 'operator') {
          return (
            <select
              key={i}
              value={part.op}
              onChange={(e) =>
                updatePart(i, { type: 'operator', op: e.target.value as '+' | '-' | '*' | '/' })
              }
            >
              {ARITH_OPS.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </select>
          );
        }
        if (part.type === 'field') {
          return (
            <select
              key={i}
              value={part.name}
              onChange={(e) => updatePart(i, { type: 'field', name: e.target.value })}
            >
              {fieldNames.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          );
        }
        // literal
        return (
          <input
            key={i}
            type="text"
            value={part.value}
            onChange={(e) => updatePart(i, { type: 'literal', value: e.target.value })}
            style={{ width: 60 }}
          />
        );
      })}
      <button type="button" onClick={addOperand}>
        + operand
      </button>
    </div>
  );
}

function BatchAggregationEditor({
  fn,
  fieldRef,
  onChange,
  fieldNames,
}: {
  fn: BatchAggFn;
  fieldRef: string;
  onChange: (source: string) => void;
  fieldNames: string[];
}) {
  return (
    <div className="compute-visual__batch">
      <select
        value={fn}
        onChange={(e) =>
          onChange(
            formulaTemplateToSource({
              kind: 'batch-aggregation',
              fn: e.target.value as BatchAggFn,
              fieldRef,
            }),
          )
        }
      >
        {BATCH_FNS.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </select>
      <select
        value={fieldRef}
        onChange={(e) =>
          onChange(
            formulaTemplateToSource({
              kind: 'batch-aggregation',
              fn,
              fieldRef: e.target.value,
            }),
          )
        }
      >
        <option value="">-- none --</option>
        {fieldNames.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </select>
    </div>
  );
}

function ConcatenationEditor({
  segments,
  onChange,
  fieldNames,
}: {
  segments: ConcatSegment[];
  onChange: (source: string) => void;
  fieldNames: string[];
}) {
  function updateSegment(index: number, updated: ConcatSegment) {
    const next = [...segments];
    next[index] = updated;
    onChange(formulaTemplateToSource({ kind: 'concatenation', segments: next }));
  }

  function addField() {
    const next: ConcatSegment[] = [
      ...segments,
      { type: 'field', name: fieldNames[0] ?? 'field' },
    ];
    onChange(formulaTemplateToSource({ kind: 'concatenation', segments: next }));
  }

  function addText() {
    const next: ConcatSegment[] = [...segments, { type: 'literal', value: '' }];
    onChange(formulaTemplateToSource({ kind: 'concatenation', segments: next }));
  }

  return (
    <div className="compute-visual__concat">
      {segments.map((seg, i) => {
        if (seg.type === 'field') {
          return (
            <select
              key={i}
              value={seg.name}
              onChange={(e) => updateSegment(i, { type: 'field', name: e.target.value })}
            >
              {fieldNames.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          );
        }
        return (
          <input
            key={i}
            type="text"
            value={seg.value}
            onChange={(e) => updateSegment(i, { type: 'literal', value: e.target.value })}
            placeholder="text"
          />
        );
      })}
      <button type="button" onClick={addField}>
        + field
      </button>
      <button type="button" onClick={addText}>
        + text
      </button>
    </div>
  );
}

function ConditionalEditor({
  clauses,
  fallback,
  onChange,
}: {
  clauses: ConditionalClause[];
  fallback: string;
  onChange: (source: string) => void;
}) {
  function updateClause(index: number, updated: ConditionalClause) {
    const next = [...clauses];
    next[index] = updated;
    onChange(formulaTemplateToSource({ kind: 'conditional', clauses: next, fallback }));
  }

  function addClause() {
    const next: ConditionalClause[] = [...clauses, { condition: '', value: '' }];
    onChange(formulaTemplateToSource({ kind: 'conditional', clauses: next, fallback }));
  }

  return (
    <div className="compute-visual__conditional">
      {clauses.map((clause, i) => (
        <div key={i} className="compute-visual__conditional-clause">
          <label>
            IF
            <input
              type="text"
              value={clause.condition}
              onChange={(e) => updateClause(i, { ...clause, condition: e.target.value })}
            />
          </label>
          <label>
            THEN
            <input
              type="text"
              value={clause.value}
              onChange={(e) => updateClause(i, { ...clause, value: e.target.value })}
            />
          </label>
        </div>
      ))}
      <button type="button" onClick={addClause}>
        + condition
      </button>
      <label>
        ELSE
        <input
          type="text"
          value={fallback}
          onChange={(e) =>
            onChange(
              formulaTemplateToSource({
                kind: 'conditional',
                clauses,
                fallback: e.target.value,
              }),
            )
          }
        />
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template picker — horizontal button strip, always visible (issue #36)
// ---------------------------------------------------------------------------

function TemplatePicker({
  currentKind,
  onSelect,
  scope,
}: {
  currentKind: TemplateKind | null;
  onSelect: (kind: TemplateKind) => void;
  scope?: 'record' | 'batch';
}) {
  const options = TEMPLATE_OPTIONS.filter((o) => !o.batchOnly || scope === 'batch');

  return (
    <div className="compute-visual__picker" role="group" aria-label="Template kind">
      <span className="compute-visual__picker-label">Template:</span>
      {options.map((o) => (
        <button
          key={o.kind}
          type="button"
          className={`compute-visual__template-btn${currentKind === o.kind ? ' is-active' : ''}`}
          aria-pressed={currentKind === o.kind}
          onClick={() => onSelect(o.kind)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ComputeVisualEditor({
  formula,
  onChange,
  fieldNames,
  scope,
}: ComputeVisualEditorProps) {
  const [selectedKind, setSelectedKind] = useState<TemplateKind | null>(null);

  const template = parseFormulaTemplate(formula);

  const handleSelectTemplate = useCallback(
    (kind: TemplateKind) => {
      setSelectedKind(kind);
      // Generate a default formula for the selected template
      let defaultTemplate: FormulaTemplate;
      switch (kind) {
        case 'static':
          defaultTemplate = { kind: 'static', value: "''" };
          break;
        case 'arithmetic':
          defaultTemplate = {
            kind: 'arithmetic',
            parts: [
              { type: 'field', name: fieldNames[0] ?? 'a' },
              { type: 'operator', op: '+' },
              { type: 'field', name: fieldNames[1] ?? 'b' },
            ],
          };
          break;
        case 'conditional':
          defaultTemplate = {
            kind: 'conditional',
            clauses: [{ condition: '', value: '' }],
            fallback: '',
          };
          break;
        case 'concatenation':
          defaultTemplate = {
            kind: 'concatenation',
            segments: [{ type: 'field', name: fieldNames[0] ?? 'field' }],
          };
          break;
        case 'batch-aggregation':
          defaultTemplate = {
            kind: 'batch-aggregation',
            fn: 'BATCH_SUM',
            fieldRef: fieldNames[0] ?? '',
          };
          break;
        case 'function':
          defaultTemplate = {
            kind: 'function',
            fn: 'LEFT',
            args: [
              { type: 'field', name: fieldNames[0] ?? 'field' },
              { type: 'literal', value: '2' },
            ],
          };
          break;
      }
      onChange(formulaTemplateToSource(defaultTemplate));
    },
    [fieldNames, onChange],
  );

  const currentKind = template?.kind ?? selectedKind ?? null;
  const isEmpty = !formula.trim() && !selectedKind;
  const isUnrecognized = formula.trim() !== '' && !template;

  return (
    <div className="compute-visual">
      <TemplatePicker
        currentKind={currentKind}
        onSelect={handleSelectTemplate}
        scope={scope}
      />
      {isEmpty && (
        <p className="compute-visual__hint">Choose a template above to start.</p>
      )}
      {isUnrecognized && (
        <p>Custom expression — use Code mode to edit</p>
      )}
      {template?.kind === 'static' && (
        <StaticEditor value={template.value} onChange={onChange} />
      )}
      {template?.kind === 'arithmetic' && (
        <ArithmeticEditor parts={template.parts} onChange={onChange} fieldNames={fieldNames} />
      )}
      {template?.kind === 'batch-aggregation' && (
        <BatchAggregationEditor
          fn={template.fn}
          fieldRef={template.fieldRef}
          onChange={onChange}
          fieldNames={fieldNames}
        />
      )}
      {template?.kind === 'concatenation' && (
        <ConcatenationEditor
          segments={template.segments}
          onChange={onChange}
          fieldNames={fieldNames}
        />
      )}
      {template?.kind === 'conditional' && (
        <ConditionalEditor
          clauses={template.clauses}
          fallback={template.fallback}
          onChange={onChange}
        />
      )}
      {template?.kind === 'function' && (
        <FunctionEditor
          template={template}
          fieldNames={fieldNames}
          onChange={onChange}
          scope={scope}
        />
      )}
    </div>
  );
}
